import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import BatteryAlertOutlinedIcon from '@mui/icons-material/BatteryAlertOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PowerOffOutlinedIcon from '@mui/icons-material/PowerOffOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import UnsubscribeOutlinedIcon from '@mui/icons-material/UnsubscribeOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import { formatDistanceToNow } from 'date-fns';
import type React from 'react';
import { getDateFnsLocale } from '../../utils/get-locale';

export type NotificationCategoryKey = 'expiry' | 'recall' | 'manual' | 'selfcheck' | 'docs' | 'iot';

export const NOTIFICATION_CATEGORY: Record<string, NotificationCategoryKey> = {
  LOT_EXPIRING: 'expiry',
  LOT_EXPIRED: 'expiry',
  DOCUMENT_EXPIRING: 'expiry',
  DOCUMENT_EXPIRED: 'expiry',
  CONSULTANT_DEADLINE_DIGEST: 'expiry',
  PRODUCT_RECALL_ALERT: 'recall',
  HACCP_MANUAL_ACTION_REQUIRED: 'manual',
  HACCP_MANUAL_PENDING_REVIEW: 'manual',
  HACCP_MANUAL_APPROVED: 'manual',
  PEST_CONTROL_CHECK_DUE: 'selfcheck',
  DOCUMENT_UPLOADED: 'docs',
  DOCUMENT_SIGNATURE_REQUESTED: 'docs',
  INGEST_RECEIVED: 'docs',
  EMAIL_DDT_RECEIVED: 'docs',
  EMAIL_DDT_MISSING: 'docs',
  EXTRACTION_FAILED: 'docs',
  TEAM_INVITE_ACCEPTED: 'docs',
  TENANT_REACTIVATED: 'docs',
  SURVEY_INVITE_ITEM_SUBMITTED: 'docs',
  IOT_TEMPERATURE_OUT_OF_RANGE: 'iot',
  IOT_HEARTBEAT_DOWN: 'iot',
  IOT_BATTERY_WARNING: 'iot',
  IOT_BATTERY_CRITICAL: 'iot',
  IOT_TEMPERATURE_BACK_IN_RANGE: 'iot',
  RECALL_CASE_OPENED: 'recall',
  RECALL_CASE_CLOSED: 'recall',
  IOT_AMBIENT_OVERHEAT: 'iot',
};

export function getNotificationCategory(type: string): NotificationCategoryKey {
  return NOTIFICATION_CATEGORY[type] ?? 'docs';
}

const NOTIFICATION_ICONS: Record<string, React.ReactElement> = {
  LOT_EXPIRING: <WarningAmberOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />,
  LOT_EXPIRED: <ErrorOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  DOCUMENT_EXPIRING: <HourglassBottomOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />,
  DOCUMENT_EXPIRED: <ErrorOutlineOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  CONSULTANT_DEADLINE_DIGEST: (
    <EventNoteOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
  ),
  PRODUCT_RECALL_ALERT: <ReportProblemOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  HACCP_MANUAL_ACTION_REQUIRED: (
    <MenuBookOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
  ),
  HACCP_MANUAL_PENDING_REVIEW: <RateReviewOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />,
  HACCP_MANUAL_APPROVED: <CheckCircleOutlinedIcon sx={{ color: 'success.main', fontSize: 20 }} />,
  PEST_CONTROL_CHECK_DUE: <BugReportOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />,
  DOCUMENT_UPLOADED: <DescriptionOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />,
  DOCUMENT_SIGNATURE_REQUESTED: (
    <HistoryEduOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />
  ),
  INGEST_RECEIVED: <UploadFileOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />,
  EMAIL_DDT_RECEIVED: <MarkEmailUnreadOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />,
  EMAIL_DDT_MISSING: <UnsubscribeOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />,
  EXTRACTION_FAILED: <ErrorOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  TEAM_INVITE_ACCEPTED: <PersonAddOutlinedIcon sx={{ color: 'success.main', fontSize: 20 }} />,
  TENANT_REACTIVATED: <ReplayOutlinedIcon sx={{ color: 'success.main', fontSize: 20 }} />,
  SURVEY_INVITE_ITEM_SUBMITTED: (
    <AssignmentTurnedInOutlinedIcon sx={{ color: 'success.main', fontSize: 20 }} />
  ),
  IOT_TEMPERATURE_OUT_OF_RANGE: (
    <DeviceThermostatOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />
  ),
  IOT_HEARTBEAT_DOWN: <WifiOffOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />,
  IOT_BATTERY_WARNING: <BatteryAlertOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />,
  IOT_BATTERY_CRITICAL: <PowerOffOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  // Recovery is good news (§6.1) — never an alarm icon/color.
  IOT_TEMPERATURE_BACK_IN_RANGE: (
    <TaskAltOutlinedIcon sx={{ color: 'success.main', fontSize: 20 }} />
  ),
  // Same icon as PRODUCT_RECALL_ALERT (spec §12): a recall case notification
  // is the same food-safety alert whether it originates from Prospero's own
  // regulatory feed or a case opened by the tenant/consultant.
  RECALL_CASE_OPENED: <ReportProblemOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  RECALL_CASE_CLOSED: <ReportProblemOutlinedIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  // Room advisory, not a cabinet breach: warning colour, no error red.
  IOT_AMBIENT_OVERHEAT: <WbSunnyOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />,
};

export function getNotificationIcon(type: string): React.ReactElement {
  return NOTIFICATION_ICONS[type] ?? <NotificationsOutlinedIcon sx={{ fontSize: 20 }} />;
}

const CATEGORY_META: Record<
  NotificationCategoryKey,
  { labelKey: string; icon: React.ReactElement; accentColor: string }
> = {
  expiry: {
    labelKey: 'notifications.filters.expiry',
    icon: <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />,
    accentColor: '#F59E0B',
  },
  recall: {
    labelKey: 'notifications.filters.recall',
    icon: <HealthAndSafetyOutlinedIcon sx={{ fontSize: 20 }} />,
    accentColor: '#DC2626',
  },
  manual: {
    labelKey: 'notifications.filters.manual',
    icon: <MenuBookOutlinedIcon sx={{ fontSize: 20 }} />,
    accentColor: '#2563EB',
  },
  selfcheck: {
    labelKey: 'notifications.filters.selfcheck',
    icon: <BugReportOutlinedIcon sx={{ fontSize: 20 }} />,
    accentColor: '#8B5CF6',
  },
  docs: {
    labelKey: 'notifications.filters.docs',
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />,
    accentColor: '#7C3AED',
  },
  iot: {
    labelKey: 'notifications.filters.iot',
    icon: <SensorsOutlinedIcon sx={{ fontSize: 20 }} />,
    accentColor: '#0D9488',
  },
};

export function getCategoryMeta(key: NotificationCategoryKey): {
  labelKey: string;
  icon: React.ReactElement;
  accentColor: string;
} {
  return CATEGORY_META[key];
}

export function formatRelativeTime(createdAt: string): string {
  return formatDistanceToNow(new Date(createdAt), { addSuffix: true, locale: getDateFnsLocale() });
}
